import { useState, useRef } from "react";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Download,
  RefreshCw,
  Layers,
  Sparkles,
  ChevronRight,
  Youtube,
  Monitor,
  Smartphone,
  CheckCircle2,
  Type,
  Package,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useStore } from "@/store/useStore";
import { ThumbnailPreview } from "@/components/ThumbnailPreview";
import { generateThumbnailCanvas } from "@/lib/canvasRenderer";

export function GeneratorPage() {
  const { teams, matches, leagues, brands, activeBrandId, incrementGeneratedCount, markMatchGenerated } = useStore();
  const [selectedMatch, setSelectedMatch] = useState("");
  const [showCompetition, setShowCompetition] = useState(false);
  const [showDateTime, setShowDateTime] = useState(false);
  const [showVenue, setShowVenue] = useState(false);
  const [showTeamNames, setShowTeamNames] = useState(false);
  const [logoSize, setLogoSize] = useState([120]);
  const [generating, setGenerating] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const thumbnailRef = useRef(null);
  const uscreenRef = useRef(null);
  const verticalRef = useRef(null);

  const currentMatch = matches.find((m) => m.id === selectedMatch);
  const pendingMatches = matches.filter((m) => !m.thumbnailGenerated);

  // Resolve the active brand: explicitly selected, else default, else null.
  const activeBrand =
    brands.find((b) => b.id === activeBrandId) ||
    brands.find((b) => b.isDefault) ||
    brands[0] ||
    null;

  const handleGenerate = async (platform) => {
    if (!selectedMatch) {
      toast.error("Please select a match first");
      return;
    }

    setGenerating(true);

    // Simulate generation delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Mark as generated
    markMatchGenerated(selectedMatch);
    incrementGeneratedCount();

    toast.success(`${platform} thumbnail generated!`, {
      description: "Click the download button to save your thumbnail.",
    });

    setGenerating(false);
  };

  // Helper function to generate a single thumbnail canvas (delegates to lib/canvasRenderer)
  const generateCanvas = (match, platform, options = {}) =>
    generateThumbnailCanvas(match, platform, {
      teams,
      leagues,
      brand: activeBrand,
      showCompetitionOpt: options.showCompetitionOpt ?? true,
    });

  const handleDownload = async (platform) => {
    if (!selectedMatch || !currentMatch) {
      toast.error("Please select a match first");
      return;
    }

    try {
      const canvas = await generateCanvas(currentMatch, platform, {
        showCompetitionOpt: showCompetition,
      });

      // Download as JPEG with compression (0.92 quality = ~90% quality, much smaller file)
      const link = document.createElement("a");
      link.download = `thumbnail_${platform}_${currentMatch?.id || "preview"}.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.92);
      link.click();

      toast.success("Thumbnail downloaded! (Compressed JPEG)");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download thumbnail");
    }
  };

  // Batch download all pending matches
  const handleBatchDownload = async (platform) => {
    if (pendingMatches.length === 0) {
      toast.error("No pending matches to generate");
      return;
    }

    setBatchGenerating(true);
    setBatchProgress({ current: 0, total: pendingMatches.length });

    // Track matches whose thumbnails were generated but had at least one logo
    // that failed to fetch (so they show a fallback initial instead of a logo).
    const flaggedMatches = [];

    try {
      const zip = new JSZip();
      const folder = zip.folder(`thumbnails_${platform}`);

      for (let i = 0; i < pendingMatches.length; i++) {
        const match = pendingMatches[i];
        setBatchProgress({ current: i + 1, total: pendingMatches.length });

        const canvas = await generateCanvas(match, platform, { showCompetitionOpt: false });

        // Convert canvas to JPEG blob with compression
        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", 0.92);
        });

        // Generate filename from team names
        const team1 = teams.find((t) => t.id === match.team1Id);
        const team2 = teams.find((t) => t.id === match.team2Id);
        const team1Name = (team1?.name || match.team1Name || "Team1").replace(/[^a-zA-Z0-9]/g, "_");
        const team2Name = (team2?.name || match.team2Name || "Team2").replace(/[^a-zA-Z0-9]/g, "_");
        const filename = `${team1Name}_vs_${team2Name}_${platform}.jpg`;

        if (canvas.__missingLogos?.length) {
          flaggedMatches.push({
            file: filename,
            missing: canvas.__missingLogos,
          });
        }

        folder.file(filename, blob);

        // Mark as generated
        markMatchGenerated(match.id);
        incrementGeneratedCount();

        // Small breather between matches so the browser can flush network
        // queues — helps prevent rare connection-pool stalls on big batches.
        await new Promise((r) => setTimeout(r, 75));
      }

      // Generate and download zip
      const content = await zip.generateAsync({ type: "blob" });
      const date = new Date().toISOString().split("T")[0];
      saveAs(content, `thumbnails_${platform}_${date}.zip`);

      if (flaggedMatches.length) {
        const list = flaggedMatches
          .map((f) => `• ${f.file} (missing: ${f.missing.join(", ")})`)
          .join("\n");
        console.warn("Thumbnails with missing logos after retries:\n" + list);
        toast.warning(
          `${pendingMatches.length} thumbnails downloaded — ${flaggedMatches.length} had missing logos. Check console for details and re-export those matches.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`${pendingMatches.length} thumbnails generated and downloaded! (Compressed)`);
      }
    } catch (error) {
      console.error("Batch download error:", error);
      toast.error("Failed to generate batch thumbnails");
    } finally {
      setBatchGenerating(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">
          Thumbnail Generator
        </h1>
        <p className="text-muted-foreground mt-1">
          Create stunning thumbnails for YouTube and UScreen
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Panel - Controls */}
        <div className="lg:col-span-1 space-y-4">
          {/* Match Selection */}
          <Card className="glass-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Layers className="w-4 h-4 text-primary" />
                Select Match
              </div>
              <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a match" />
                </SelectTrigger>
                <SelectContent>
                  {pendingMatches.length > 0 ? (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        Pending Matches
                      </div>
                      {pendingMatches.map((match) => (
                        <SelectItem key={match.id} value={match.id}>
                          <div className="flex items-center gap-2">
                            {match.team1Name || "TBD"} vs {match.team2Name || "TBD"}
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  ) : null}
                  {matches.filter((m) => m.thumbnailGenerated).length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        Generated
                      </div>
                      {matches
                        .filter((m) => m.thumbnailGenerated)
                        .map((match) => (
                          <SelectItem key={match.id} value={match.id}>
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-success" />
                              {match.team1Name || "TBD"} vs {match.team2Name || "TBD"}
                            </div>
                          </SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {matches.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No matches found. Add matches first.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Branding Selector */}
          <Card className="glass-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between gap-2 text-sm font-medium text-foreground">
                <span>Branding</span>
                {activeBrand?.id && activeBrand.id !== (brands.find((b) => b.isDefault)?.id || null) && (
                  <button
                    type="button"
                    className="text-[11px] text-primary hover:underline"
                    onClick={() => useStore.getState().setActiveBrand(null)}
                    data-testid="brand-reset-default"
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <Select
                value={activeBrand?.id || ""}
                onValueChange={(v) => useStore.getState().setActiveBrand(v)}
              >
                <SelectTrigger data-testid="brand-selector">
                  <SelectValue placeholder="Pick a brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      <span className="inline-flex items-center gap-2">
                        {b.logo && (
                          <img src={mediaUrl(b.logo)} alt="" className="w-4 h-4 object-contain"  loading="lazy" decoding="async"/>
                        )}
                        {b.name}
                        {b.isDefault && (
                          <span className="text-[10px] text-muted-foreground">· default</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {brands.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No brands yet. Add one in the Branding page.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Display Options */}
          <Card className="glass-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Type className="w-4 h-4 text-primary" />
                Display Options
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Show Competition</Label>
                  <Switch checked={showCompetition} onCheckedChange={setShowCompetition} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Show Date & Time</Label>
                  <Switch checked={showDateTime} onCheckedChange={setShowDateTime} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Show Team Names</Label>
                  <Switch checked={showTeamNames} onCheckedChange={setShowTeamNames} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Show Venue</Label>
                  <Switch checked={showVenue} onCheckedChange={setShowVenue} />
                </div>
              </div>
              <div className="pt-2">
                <Label className="text-sm text-muted-foreground mb-2 block">
                  Logo Size: {logoSize}%
                </Label>
                <Slider
                  value={logoSize}
                  onValueChange={setLogoSize}
                  min={50}
                  max={120}
                  step={5}
                />
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="glass-card">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-foreground mb-2">Thumbnail Style</p>
              <p className="text-xs text-muted-foreground">
                Thumbnails use team colors for gradient backgrounds. The league logo appears in the center between team logos, matching the Peacock TV sports thumbnail style.
              </p>
            </CardContent>
          </Card>

          {/* Batch Download Card */}
          {pendingMatches.length > 0 && (
            <Card className="glass-card border-primary/30">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Package className="w-4 h-4 text-primary" />
                  Batch Download
                </div>
                <p className="text-xs text-muted-foreground">
                  Generate all {pendingMatches.length} pending match thumbnails at once and download as a ZIP file.
                </p>
                {batchGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Generating...</span>
                      <span>{batchProgress.current} / {batchProgress.total}</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => handleBatchDownload("youtube")}
                    disabled={batchGenerating}
                  >
                    {batchGenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Youtube className="w-4 h-4" />
                    )}
                    YouTube (1920×1080)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => handleBatchDownload("uscreen")}
                    disabled={batchGenerating}
                  >
                    {batchGenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Monitor className="w-4 h-4" />
                    )}
                    UScreen (1480×840)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => handleBatchDownload("uscreen-vertical")}
                    disabled={batchGenerating}
                  >
                    {batchGenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Smartphone className="w-4 h-4" />
                    )}
                    Vertical (1188×1682)
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="glass-card">
            <CardContent className="p-5">
              <Tabs defaultValue="youtube" className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <TabsList className="bg-secondary">
                    <TabsTrigger value="youtube" className="gap-2">
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </TabsTrigger>
                    <TabsTrigger value="uscreen" className="gap-2">
                      <Monitor className="w-4 h-4" />
                      UScreen
                    </TabsTrigger>
                    <TabsTrigger value="uscreen-vertical" className="gap-2">
                      <Smartphone className="w-4 h-4" />
                      UScreen Vertical
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => setSelectedMatch("")}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset
                    </Button>
                  </div>
                </div>

                <TabsContent value="youtube" className="mt-0">
                  <ThumbnailPreview
                    ref={thumbnailRef}
                    match={currentMatch}
                    showCompetition={showCompetition}
                    showDateTime={showDateTime}
                    showVenue={showVenue}
                    showTeamNames={showTeamNames}
                    logoSize={logoSize[0]}
                    platform="youtube"
                    brand={activeBrand}
                  />
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="premium"
                      className="flex-1 gap-2"
                      onClick={() => handleGenerate("YouTube")}
                      disabled={generating || !selectedMatch}
                    >
                      {generating ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      Generate
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => handleDownload("youtube")}
                      disabled={!selectedMatch}
                    >
                      <Download className="w-4 h-4" />
                      Download (1920×1080)
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="uscreen" className="mt-0">
                  <ThumbnailPreview
                    ref={uscreenRef}
                    match={currentMatch}
                    showCompetition={showCompetition}
                    showDateTime={showDateTime}
                    showVenue={showVenue}
                    showTeamNames={showTeamNames}
                    logoSize={logoSize[0]}
                    platform="uscreen"
                    brand={activeBrand}
                  />
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="premium"
                      className="flex-1 gap-2"
                      onClick={() => handleGenerate("UScreen")}
                      disabled={generating || !selectedMatch}
                    >
                      {generating ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      Generate
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => handleDownload("uscreen")}
                      disabled={!selectedMatch}
                    >
                      <Download className="w-4 h-4" />
                      Download (1480×840)
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="uscreen-vertical" className="mt-0">
                  <div className="flex justify-center">
                    <div className="w-64">
                      <ThumbnailPreview
                        ref={verticalRef}
                        match={currentMatch}
                        showCompetition={showCompetition}
                        showDateTime={showDateTime}
                        showVenue={showVenue}
                        showTeamNames={showTeamNames}
                        logoSize={logoSize[0]}
                        platform="uscreen-vertical"
                        brand={activeBrand}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="premium"
                      className="flex-1 gap-2"
                      onClick={() => handleGenerate("UScreen Vertical")}
                      disabled={generating || !selectedMatch}
                    >
                      {generating ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      Generate
                    </Button>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => handleDownload("uscreen-vertical")}
                      disabled={!selectedMatch}
                    >
                      <Download className="w-4 h-4" />
                      Download (1188×1682)
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Quick Info */}
          {currentMatch && (
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {currentMatch.team1Name || "TBD"} vs {currentMatch.team2Name || "TBD"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {currentMatch.competition && `${currentMatch.competition} • `}
                      {currentMatch.date &&
                        new Date(currentMatch.date).toLocaleDateString()}
                      {currentMatch.time && ` at ${currentMatch.time}`}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
